import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Eye, Trash2, PenLine, FileDown, Navigation } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { TabsContent } from "@/components/ui/tabs";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "@/components/lib/Pagination";
import { setStatus, setPerPage, setCurrentPage, setVoucherSearch, clearVoucherMessages, deleteConcessionById } from "@/redux/slice/loyalty/ConcessionSlice";
import { Button } from "@/components/ui/button";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import ViewConcession from "./ViewConcession";
import CommonSendVia from "../CommonSendVia";
import { printPdfFromUrl } from "@/lib/download";
import { sendCompensationNotification } from "@/api/new-api/order-history/order-history";

function ConcessionTab() {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const isInitialRender = useRef(true);
  const [itemsDialog, setItemsDialog] = useState(null);
  const { status, search, vouchers, perPage, currentPage, totalPages } = useSelector(
    (state) => state.loyalty.concession
  );
  // Focus on input after data loads (but not on first render)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (inputRef.current && search) {
      inputRef.current.focus()
      dispatch(setCurrentPage(1))
    }
  }, [vouchers]);

  // Handlers
  const handleSearchChange = useCallback(
    (e) => dispatch(setVoucherSearch(e.target.value)),
    [dispatch]
  );

  // Delete 
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const closeDeleteDialog = () => {
    setDeleteDialog(false);
  };
  const deleteHandler = async (id) => {
    try {
      if (!id) {
        toast.error("ID is required to delete.");
        return;
      }
      dispatch(clearVoucherMessages());
      const res = await dispatch(deleteConcessionById(id));
      if (deleteConcessionById.fulfilled.match(res)) {
        toast.success("Concession deleted successfully.");
        setDeleteDialog(false);
        dispatch(clearVoucherMessages());
      } else if (deleteConcessionById.rejected.match(res)) {
        toast.error(res?.payload);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!.");
    } finally {
      closeDeleteDialog();
    }
  };

  // Add customer
  const [selectedSendVoucherViaOptions, setSelectedSendVoucherViaOptions] = useState([]);
  // View customer
  const [isSingleVoucherModalOpen, setIsSingleVoucherModalOpen] = useState(false);
  const closeSingleVoucher = () => {
    setIsSingleVoucherModalOpen(false); 
    setItemsDialog(null)
  };

  // Send voucher via
  const [isSendVoucherViaModalOpen, setIsSendVoucherViaModalOpen] = useState(false);
  const closeSendVoucherVia = () => {
    setIsSendVoucherViaModalOpen(false); 
    setItemsDialog(null)
  };
  const handleConfirm = async (selected) => {
    if (selected?.includes("Print")) {
      const pdfUrl = itemsDialog?.pdf_url || itemsDialog?.voucher?.pdf_url || null;
      if (pdfUrl) {
        printPdfFromUrl(pdfUrl);
        toast.success("Voucher opened for printing.");
        return true;
      }
      toast.error("PDF URL not found for printing.");
      return false;
    }

    if (selected?.includes("EMAIL")) {
      const orderId = itemsDialog?.complain_order_id;
      if (!orderId) {
        toast.error("Complain Order ID is missing for this concession.");
        return false;
      }
      try {
        const res = await sendCompensationNotification(orderId, {
          customer_id: itemsDialog?.customer_id || null,
          channel: "email",
        });
        if (res?.status === "success" || res?.status === "Success" || !res?.status) {
          toast.success(res?.message || "Compensation email sent successfully!");
          return true;
        } else {
          toast.error(res?.message || "Failed to send email.");
          return false;
        }
      } catch (error) {
        toast.error(error?.message || "An error occurred while sending email.");
        return false;
      }
    }

    return true;
  };
  return (
    <TabsContent value="concession">
      <div className="mt-5">
        <div className="w-full flex justify-between lg:flex-row flex-col gap-5 items-start lg:items-center">
          <div className="w-[260px]">
            <div className="relative">
              <Search
                size={18}
                className="text-cwhite3 absolute top-2 left-2"
              />
              <Input
                ref={inputRef}
                value={search}
                onChange={handleSearchChange}
                placeholder="Search"
                className="bg-cblack3 pl-8"
              />
            </div>
          </div>

          <div className="flex flex-col  gap-1.5 justify-end items-end">
            <div className="flex gap-3 justify-center items-center">
              <div className="relative">
                <Select
                  value={status}
                  onValueChange={(value) => {
                  dispatch(setStatus(value === "0" ? "" : value))
                  dispatch(setCurrentPage(1))
                }}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">All</SelectItem>
                    <SelectItem value="used">Used</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                className={"bg-cblack3 border border-gray-600 text-cwhite2"}
              >
                <FileDown /> Export
              </Button>
              <div className="flex gap-1 justify-center items-center">
                <h3 className="text-white">Show</h3>
                <Select
                  value={String(perPage)}
                  onValueChange={(value) => {
                    dispatch(setPerPage(Number(value)))
                    dispatch(setCurrentPage(1))
                  }}
                >
                  <SelectTrigger className="w-[60px]">
                    <SelectValue placeholder={String(perPage)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="40">40</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
        {/* table start */}
        <Table className="w-full mt-5">
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Order ID (Origin)</TableHead>
              <TableHead>Order ID (Claim)</TableHead>
              <TableHead>Customer ID</TableHead>
              <TableHead>Customer Name</TableHead>
              <TableHead>Complain Notes</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Used At</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vouchers && vouchers?.length > 0 ? (
              vouchers?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium text-cwhite">{item?.code || "-"}</TableCell>
                  <TableCell>{item?.complain_order_id || "-"}</TableCell>
                  <TableCell>{item?.claim_order_id || "-"}</TableCell>
                  <TableCell>{item?.customer_id || "-"}</TableCell>
                  <TableCell>{item?.customer_full_name || "-"}</TableCell>
                  <TableCell>{item?.complain_note || "-"}</TableCell>
                  
                  <TableCell>
                    {item?.reward_amount ? `£${parseFloat(item.reward_amount)}` : "-"}
                  </TableCell>
                  <TableCell>{ item?.used_at}</TableCell>
                  <TableCell>
                    {item?.claim_order_id || item?.status === "used" || item?.used_at ? (
                      <span className="text-green-500 font-medium">Used</span>
                    ) : item?.status === "active" ? (
                      <span className="text-green-500 font-medium">Active</span>
                    ) : (
                      <span className="text-yellow-500 font-medium">Pending</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right justify-end flex gap-2 items-center">
                    <div className="flex items-center gap-3 h-8">
                      <Navigation
                        onClick={() => {
                          setIsSendVoucherViaModalOpen(true)
                          setItemsDialog(item);
                        }}
                        className="text-cwhite3 hover:text-white cursor-pointer"
                        size={20}
                      />

                      <span>
                        {
                          item?.status !== "used" && (
                            <Trash2
                              onClick={() => {
                                setDeleteDialog(true);
                                setSelectedId(item?.id);
                          }}
                          className="text-cred hover:text-red-400 cursor-pointer"
                          size={20}
                        />
                      )}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} className="text-center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <div className="flex justify-start mt-4">
          <Pagination
              currentPage={currentPage} 
              totalPages={totalPages} 
              onPageChange={(e)=>dispatch(setCurrentPage(e))} 
          />
        </div>
      </div>

      <DeleteConfirmDialog
        onConfirm={() => {
          deleteHandler(selectedId);
        }}
        isOpen={deleteDialog}
        onClose={closeDeleteDialog}
      />
      <CommonSendVia
        isOpen={isSendVoucherViaModalOpen}
        onClose={closeSendVoucherVia}
        title="Send Concession Code Via"
        options={[
          // "Whatsapp",
          // "SMS",
          "EMAIL",
          "Print",
        ]}
        value={selectedSendVoucherViaOptions}
        onChange={setSelectedSendVoucherViaOptions}
        onPrint={async () => {
          const pdfUrl =
            itemsDialog?.pdf_url ||
            itemsDialog?.voucher?.pdf_url ||
            null;

          if (!pdfUrl) {
            toast.error("PDF URL not found for printing.");
            return false;
          }

          printPdfFromUrl(pdfUrl);
          toast.success("Voucher opened for printing.");
          return true;
        }}
        onConfirm={handleConfirm}
      />

      <ViewConcession
        isOpen={isSingleVoucherModalOpen}
        onClose={closeSingleVoucher}
        selectedVoucher={itemsDialog}
      />
    </TabsContent>
  );
}

export default ConcessionTab;